'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Building2 } from 'lucide-react';
import { FilterGroup } from '../FilterGroup';
import type { RechtsformOption } from '../searchConstants';
import type { StagedFilters, StagedFilterSetters } from '../useSidebarFilterState';
import {
  CompanySizeField,
  ContactField,
  FoundedBetweenField,
  LegalStatusField,
  MembershipsField,
  RechtsformField,
  TenderRoleField,
} from './ProfileFilterGroupParts';

export interface ProfileFilterGroupProps {
  t: TFunction<'translation', undefined>;
  profileCount: number;
  clearProfile: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | 'pro' | null;
  /** The staged filter values and their setters (useSidebarFilterValues). */
  staged: StagedFilters;
  set: StagedFilterSetters;
  rechtsformOpen: boolean;
  setRechtsformOpen: Dispatch<SetStateAction<boolean>>;
  removeSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  rechtsformOptions: RechtsformOption[];
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  facetSum: (facet: string, values: string[]) => number | undefined;
  handleFormSubmit: () => void;
  lockOf: (control: string) => 'user' | 'pro' | null;
  membershipsOpen: boolean;
  setMembershipsOpen: Dispatch<SetStateAction<boolean>>;
  membershipOptions: { value: string; label: string }[];
  facetCount: (facet: string, value: string) => number | undefined;
}

export function ProfileFilterGroup(props: ProfileFilterGroupProps) {
  const {
    t,
    profileCount,
    clearProfile,
    openFilterGroup,
    handleGroupOpenChange,
    groupLock,
  } = props;
  return (
    <FilterGroup
      id="profile"
      icon={Building2}
      label={t('search.sidebar.groups.profile', {
        defaultValue: 'Company profile',
      })}
      activeCount={profileCount}
      onClear={clearProfile}
      open={openFilterGroup === 'profile'}
      onOpenChange={handleGroupOpenChange('profile')}
      lock={groupLock('profile')}
    >
      <RechtsformField {...props} />
      <FoundedBetweenField {...props} />
      <MembershipsField {...props} />
      <LegalStatusField {...props} />
      <CompanySizeField {...props} />
      <TenderRoleField {...props} />
      <ContactField {...props} />
      {/* A new profile filter: a field component in
          ProfileFilterGroupParts.tsx reading staged.<key> / set.<key>
          (OptionCheckboxField for a checkbox list with an ⓘ; a single
          boolean is one PROFILE_FLAGS entry), rendered here in sidebar
          order. */}
    </FilterGroup>
  );
}
